import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'

import { APP_ENV } from '@/config/env'
import { apiBases, getApiService } from '@/services/api'
import { getAppendedFullUrl, getQueryParams, removeImpermanentSearchParams } from '@/utils/url'

const BUYING_FLOW_HOSTNAME =
  APP_ENV !== 'production' ? 'start.gtsf.insure' : 'start.hellogetsafe.com'

export type LiabilityConfigParams = { family_coverage: boolean }
type FlowParams =
  | { flowName: 'liability'; configParams: LiabilityConfigParams }
  | { flowName: 'contents'; configParams: ContentsConfigParams }

export type StartFlowSessionParams = { lang: Language; market: SupportedMarketKey } & FlowParams

export type ContentsConfigParams = {
  glass_coverage: boolean
  living_space_square_meters: number
}

export const getBuyingFlowSessionUrl = ({
  lang,
  flowName,
  sessionId,
  market,
}: Pick<StartFlowSessionParams, 'lang' | 'market' | 'flowName'> & { sessionId?: string }) => {
  const baseLink = `https://${BUYING_FLOW_HOSTNAME}/${lang}-${market}/flows/${flowName}${
    sessionId ? `?session=${sessionId}` : ''
  }`

  const params = getQueryParams(window.location.href)

  const whitelistParams = removeImpermanentSearchParams(params).toString()

  return getAppendedFullUrl(baseLink, whitelistParams)
}

const getLiabilitySessionData = (configParams: LiabilityConfigParams) => ({
  flowValues: {
    products: {
      liability_v1_getsafe_de: {
        productKey: 'liability_v1_getsafe_de',
        configuration_parameters: configParams,
      },
    },
  },
  flowState: {
    currentStep: 'liability_instant',
    history: [
      {
        stepName: 'liability_instant',
        stepSubFlowName: 'liability-nxt',
        stepState: 'STARTED',
      },
    ],
  },
})

const getContentsSessionData = (configParams: ContentsConfigParams) => ({
  flowValues: {
    products: {
      contents_v1_getsafe_de: {
        productKey: 'contents_v1_getsafe_de',
        configuration_parameters: configParams,
      },
    },
  },
  flowState: {
    currentStep: 'contents_instant',
    history: [
      {
        stepName: 'contents_instant',
        stepSubFlowName: 'contents-nxt',
        stepState: 'STARTED',
      },
    ],
  },
})

const getFlowSessionData = ({ lang, market, flowName, configParams }: StartFlowSessionParams) => {
  let data

  switch (flowName) {
    case 'liability':
      data = getLiabilitySessionData(configParams)
      break
    case 'contents':
      data = getContentsSessionData(configParams)
      break
    default:
      throw new Error(`${flowName} is not supported`)
  }

  return {
    flow_type: flowName,
    language: lang,
    market: market,
    client_type: 'web',
    version: 59, // To be updated to use constant
    data,
  }
}

export const startFlowSession = (params: StartFlowSessionParams) => {
  const apiService = getApiService({ language: params.lang })
  const data = getFlowSessionData(params)
  return apiService.client.post('/v1/flow_sessions', data, { baseURL: apiBases.user })
}
